import React, { useContext, useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import NarrowSecureLayout from "../../../layout/NarrowSecureLayout.jsx";
import Modal from "../../../components/Modal.jsx";
import { ProjectApi } from "../../../api/authority/generated/index";
import useAuthorityConfiguration from "../../../api/utilities/useAuthorityConfiguration.js";
import { useErrorTranslation } from "../../../api/utilities/translateErrorMessage.js";
import toast from "react-hot-toast";
import ProjectDomain from "./ProjectDomain.jsx";
import { GlobalContext } from "../../../utils/context/GlobalContextProvider.jsx";
import DomainsConfirmationModal from "./DomainsConfirmationModal.jsx";
import Button from "../../../components/Button.jsx";
import LoadingComponent from "../../../components/LoadingComponent.jsx";
import SuperadminIndicator from "../../../components/auth/SuperadminIndicator.jsx";
import {useIsAdministrator} from "../../../components/auth/rolesAndPermissions.js";
import Creatable from "react-select/creatable";
import {useNavigate} from "react-router-dom";

const splitHostNames = (value) => value
    .split(/[\s,;]+/)
    .map((hostName) => hostName.trim())
    .filter(Boolean);

const uniqueHostNames = (hostNames) => [...new Map(
    hostNames.map((hostName) => [hostName.toLowerCase(), hostName]),
).values()];

export default function ProjectDomains({ project }) {
    const { t } = useTranslation();
    const { et } = useErrorTranslation();
    const authorityConfiguration = useAuthorityConfiguration();
    const { context } = useContext(GlobalContext);
    const isAdministrator = useIsAdministrator();
    const navigate = useNavigate();

    // State hooks for managing domains, changes, modals, loading, and price/payment info
    const [domainsWithStats, setDomainsWithStats] = useState([]);
    const [domainsLoading, setDomainsLoading] = useState(true);
    const [changedDomains, setChangedDomains] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [addDomainModalOpened, setAddDomainModalOpened] = useState(false);
    const [addDomainText, setAddDomainText] = useState('');
    const [addDomainLoading, setAddDomainLoading] = useState(false);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const [separateDomainHostNames, setSeparateDomainHostNames] = useState(project.separateDomainHostNames ?? []);
    const [separateDomainsSaving, setSeparateDomainsSaving] = useState(false);
    const [domainPriceQuote, setDomainPriceQuote] = useState(null);
    const [domainsToPay, setDomainsToPay] = useState(0);
    const subscription = project.subscription;

    useEffect(() => {
        setSeparateDomainHostNames(project.separateDomainHostNames ?? []);
    }, [project.id]);

    const handleSaveSeparateDomains = async () => {
        setSeparateDomainsSaving(true);
        try {
            await new ProjectApi(authorityConfiguration).updateProject({
                projectId: project.id,
                projectInput: {
                    name: project.name,
                    separateDomainHostNames,
                },
            });
            toast.success(t('saved'));
        } catch (error) {
            toast.error(await et(error));
        } finally {
            setSeparateDomainsSaving(false);
        }
    };

    const handleUpgradePlan = () => {
        navigate(`/project/${project.id}/settings/subscription`);
    };

    // Refs to prevent unwanted repeated operations (for React Strict Mode)
    const isInitialMount = useRef(true);
    const fetchInitiatedRef = useRef(new Map());
    const fetchGenerationRef = useRef(0);
    const domainUpdateInFlightRef = useRef(false);

    // For free or missing subscription, only allow one domain to be enabled
    const isFreePackage = !subscription || subscription?.currentPackage?.code === "free";

    // Calculate the count of currently enabled domains (including unsaved state)
    const selectedEnabled = useMemo(() => {
        return Object.values(domainsWithStats).filter((d) =>
            changedDomains[d.projectDomain.hostName] !== undefined
                ? changedDomains[d.projectDomain.hostName]
                : d.projectDomain.isEnabled
        ).length;
    }, [domainsWithStats, changedDomains]);

    // Maximum domains allowed by current subscription
    const maximumDomains = useMemo(() => {
        return subscription?.currentPackage?.maxDomains ?? context.defaultSubscriptionValues.maxDomains;
    }, [project.id, subscription]);

    // Fetches project domains from API and handles possible errors
    const fetchDomains = async () => {
        const generation = ++fetchGenerationRef.current;
        setDomainsWithStats([]);
        setChangedDomains({});
        setDomainsLoading(true);
        try {
            const response = await new ProjectApi(authorityConfiguration).listProjectDomains({
                projectId: project.id,
            });
            if (generation !== fetchGenerationRef.current) return;
            setDomainsWithStats(response || []);
            setDomainsLoading(false);
        } catch (e) {
            if (generation !== fetchGenerationRef.current) return;
            et(e).then((message) => { if (message) toast.error(message); });
        }
    };

    // Loads domains when project is ready or changes
    useEffect(() => {
        if (!project?.id) return;

        const fetchData = async () => {
            // Avoid double-fetching, especially in React StrictMode
            if (isInitialMount.current && fetchInitiatedRef.current.has(project.id)) {
                return;
            }
            if (isInitialMount.current) {
                fetchInitiatedRef.current.set(project.id, true);
            }
            await fetchDomains();
        };

        fetchData();

        if (isInitialMount.current) {
            isInitialMount.current = false;
        }

        // Cleanup (removes completed flag when component unmounts)
        return () => {
            fetchGenerationRef.current += 1;
            fetchInitiatedRef.current.delete(project.id);
        };
    }, [project?.id]);

    // Handles the addition of new domain when loading flag is set
    useEffect(() => {
        if (!addDomainLoading) return;

        new ProjectApi(authorityConfiguration)
            .createOrUpdateProjectDomain({
                projectId: project.id,
                createOrUpdateProjectDomainInput: {
                    isEnabled: false,
                    hostName: addDomainText,
                },
            })
            .then(() => {
                setAddDomainText('');
                setAddDomainModalOpened(false);
                fetchDomains();
            })
            .catch((e) => et(e).then((message) => { if (message) toast.error(message); }))
            .finally(() => setAddDomainLoading(false));
    }, [addDomainLoading, project.id, authorityConfiguration, et, addDomainText]);

    // Handle logic for toggles, allow only one enabled when free or missing subscription
    const handleToggleChange = (hostName, newValue) => {
        if (isFreePackage && newValue) {
            // If any other domain is already enabled, do not allow enabling this one
            const anotherEnabled = Object.values(domainsWithStats)
                .some((d) =>
                    (changedDomains[d.projectDomain.hostName] !== undefined
                        ? changedDomains[d.projectDomain.hostName]
                        : d.projectDomain.isEnabled
                    ) &&
                    d.projectDomain.hostName !== hostName
                );
            if (anotherEnabled) {
                return;
            }
        }
        setChangedDomains((prev) => {
            const newChangedDomains = { ...prev };
            const originalDomain = Object.values(domainsWithStats).find(
                (d) => d.projectDomain.hostName === hostName
            );
            if (originalDomain && originalDomain.projectDomain.isEnabled === newValue) {
                delete newChangedDomains[hostName];
            } else {
                newChangedDomains[hostName] = newValue;
            }
            return newChangedDomains;
        });
    };

    // Save changes: check prices before commit, open confirmation if needed
    const handleSaveChanges = () => {
        setIsSaving(true);

        const domainUpdates = Object.entries(changedDomains).map(([hostName, isEnabled]) => ({
            hostName,
            isEnabled,
        }));

        // Gets possible additional price for requested change
        new ProjectApi(authorityConfiguration)
            .getDomainsStatusChangePrice({
                projectId: project.id,
                createOrUpdateProjectDomainInput: domainUpdates,
            })
            .then((response) => {
                setDomainsToPay(response.domainsToPay);
                const price = response.totalAmount;
                if (price > 0) {
                    setDomainPriceQuote(response);
                    setIsConfirmationModalOpen(true);
                    setIsSaving(false);
                } else {
                    performDomainUpdate(domainUpdates);
                }
            })
            .catch((e) => {
                et(e).then((message) => { if (message) toast.error(message); });
                setIsSaving(false);
            });
    };

    // Perform the actual update/commit of domain changes
    const performDomainUpdate = async (domainUpdates) => {
        if (domainUpdateInFlightRef.current) return false;

        domainUpdateInFlightRef.current = true;
        setIsSaving(true);
        try {
            await new ProjectApi(authorityConfiguration).updateProjectDomainsStatus({
                projectId: project.id,
                createOrUpdateProjectDomainInput: domainUpdates,
            });
            setChangedDomains({});
            toast.success(t('project.domains-updated'));
            await fetchDomains();
            return true;
        } catch (e) {
            const message = await et(e);
            if (message) toast.error(message);
            return false;
        } finally {
            domainUpdateInFlightRef.current = false;
            setIsSaving(false);
        }
    };

    // Confirms price-based domain change in modal
    const handleConfirmDomainChange = async () => {
        const domainUpdates = Object.entries(changedDomains).map(([hostName, isEnabled]) => ({
            hostName,
            isEnabled,
        }));
        const updated = await performDomainUpdate(domainUpdates);
        if (updated) setIsConfirmationModalOpen(false);
    };

    // Cancels any changes (reset state)
    const handleCancelChanges = () => {
        setChangedDomains({});
    };

    // Check if there are any unsaved changes
    const hasChanges = Object.keys(changedDomains).length > 0;

    // State for disabling save button on FREE
    const isSaveDisabled = useMemo(() => {
        if (!hasChanges || isSaving) return true;
        if (isFreePackage) {
            // Allow button only if exactly one domain is enabled
            // Taking into account changes and current state
            return selectedEnabled !== 1;
        }
        return false;
    }, [hasChanges, isSaving, isFreePackage, selectedEnabled]);

    const [primaryDomains, secondaryDomains] = useMemo(() => {
        const sortDomains = (domains) => [...domains].sort((left, right) =>
            right.projectDomainStats.orderCount - left.projectDomainStats.orderCount
            || left.projectDomain.hostName.localeCompare(right.projectDomain.hostName)
        );

        const primary = [];
        const secondary = [];
        domainsWithStats.forEach((domainWithStats) => {
            (domainWithStats.projectDomain.isBuiltin === false || domainWithStats.projectDomainStats.orderCount > 0
                ? primary
                : secondary
            ).push(domainWithStats);
        });

        return [sortDomains(primary), sortDomains(secondary)];
    }, [domainsWithStats]);

    const renderDomain = (domainWithStats) => {
        const isThisDisabled =
            isFreePackage && (
                !(
                    changedDomains[domainWithStats.projectDomain.hostName] !== undefined
                        ? changedDomains[domainWithStats.projectDomain.hostName]
                        : domainWithStats.projectDomain.isEnabled
                ) &&
                selectedEnabled >= 1
            );
        const isEnabled = changedDomains[domainWithStats.projectDomain.hostName] !== undefined
            ? changedDomains[domainWithStats.projectDomain.hostName]
            : domainWithStats.projectDomain.isEnabled;

        return (
            <ProjectDomain
                key={domainWithStats.projectDomain.hostName}
                project={project}
                domainWithStats={domainWithStats}
                onReload={fetchDomains}
                onToggleChange={handleToggleChange}
                isSaving={isSaving}
                isDisabled={isThisDisabled}
                isEnabled={isEnabled}
            />
        );
    };

    return [
        <NarrowSecureLayout
            key={'pd-page-content'}
            title={
                <>
                    {t('project.domains')}
                    {domainsWithStats && (
                        <span className="text-sm text-violet-500 self-end mb-1">
                            {selectedEnabled}/{maximumDomains}
                        </span>
                    )}
                </>
            }
            actions={
                <Button
                    variant="black"
                    onClick={() => setAddDomainModalOpened(true)}
                    disabled={isFreePackage && domainsWithStats.length > 0} // button is always visible but not clickable for free/missing subscription
                >
                    {t('project.add-domain')}
                </Button>
            }
        >
            {isFreePackage && (
                <div
                    className="mb-4 md:-mt-4 w-full h-full flex w-full text-normal text-black justify-between items-center text-left py-3 px-4 rounded-lg bg-violet-100 shadow-sm border border-violet-300 transition"
                >
                    {t('project.no-active-package-text')}
                    <Button type="button" variant="outline-violet" onClick={handleUpgradePlan}>
                        {t('domains.change-tarif')}
                    </Button>
                </div>
            )}

            <div className={'mb-6 rounded-lg border border-violet-200 bg-violet-50 p-4 text-sm text-violet-900 dark:border-violet-900/60 dark:bg-violet-950/30 dark:text-violet-100'}>
                <div className={'font-semibold'}>{t('project.domains-description-title')}</div>
                <div className={'mt-1 text-violet-800 dark:text-violet-200'}>{t('project.domains-description')}</div>
            </div>

            {isAdministrator && (
                <div className="mb-6 rounded-xl border border-violet-200 bg-violet-50/70 p-4 dark:border-violet-500/30 dark:bg-violet-500/10">
                    <div>
                        <div className="flex items-center justify-between gap-3">
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                                {t('project.separate-domain-host-names')}
                            </label>
                            <SuperadminIndicator/>
                        </div>
                        <div className="integration-detail-form mt-1.5">
                            <Creatable
                                classNamePrefix="select"
                                isMulti
                                isClearable
                                options={[]}
                                value={separateDomainHostNames.map((hostName) => ({value: hostName, label: hostName}))}
                                placeholder={t('project.separate-domain-host-names-placeholder')}
                                formatCreateLabel={(value) => t('project.separate-domain-host-names-add', {value})}
                                noOptionsMessage={() => null}
                                onChange={(options) => setSeparateDomainHostNames(
                                    uniqueHostNames(options.map((option) => option.value)),
                                )}
                                onCreateOption={(inputValue) => setSeparateDomainHostNames((current) =>
                                    uniqueHostNames([...current, ...splitHostNames(inputValue)]),
                                )}
                            />
                        </div>
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                            {t('project.separate-domain-host-names-description')}
                        </p>
                        <div className="mt-4 flex justify-end">
                            <Button onClick={handleSaveSeparateDomains} loading={separateDomainsSaving}>
                                {t('save')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            <LoadingComponent loading={domainsLoading}>
            {primaryDomains.length > 0 && (
                <section className={'mb-6'}>
                    <h2 className={'mb-3 text-lg font-semibold'}>{t('project.primary-domains')}</h2>
                    {primaryDomains.map(renderDomain)}
                </section>
            )}

            {secondaryDomains.length > 0 && (
                <section>
                    <h2 className={'mb-3 text-lg font-semibold'}>{t('project.secondary-domains')}</h2>
                    {secondaryDomains.map(renderDomain)}
                </section>
            )}

            {hasChanges && (
                <div className={'flex justify-end mt-6 gap-2'}>
                    <Button
                        variant="outline-black"
                        onClick={handleCancelChanges}
                        disabled={isSaving}
                    >
                        {t('domains.discard-changes')}
                    </Button>
                    <Button
                        variant="black"
                        onClick={handleSaveChanges}
                        loading={isSaving}
                        disabled={isSaveDisabled}
                    >
                        {isSaving ? t('saving') : t('save-changes')}
                    </Button>
                </div>
            )}
            </LoadingComponent>

            {/* Add domain modal */}
            <Modal
                key={'pd-add-domain-modal'}
                className={'w-full lg:w-11/12 max-w-lg'}
                visible={addDomainModalOpened}
                title={t('project.add-domain')}
                setVisible={setAddDomainModalOpened}
                loading={addDomainLoading}
            >
                <div className={'flex flex-col justify-end gap-4 xl:flex-row'}>
                    <div className={'flex justify-center items-center w-full'}>
                        https://
                        <input
                            className={'form-input flex-grow'}
                            type={'text'}
                            value={addDomainText}
                            onChange={(e) => setAddDomainText(e.target.value)}
                        />/
                    </div>
                    <div className={'flex'}>
                        <Button
                            variant="black"
                            className={'ml-auto'}
                            onClick={() => setAddDomainLoading(true)}
                        >
                            {t('project.add-domain')}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Confirmation modal for possible extra price */}
            <DomainsConfirmationModal
                key="pd-confirmation-modal"
                isOpen={isConfirmationModalOpen}
                onConfirm={handleConfirmDomainChange}
                onClose={() => setIsConfirmationModalOpen(false)}
                priceQuote={domainPriceQuote}
                domainsToPay={domainsToPay}
                project={project}
            />
        </NarrowSecureLayout>
    ];
}
